/* Progressive motion, scoped to each section and safe for Shopify editor reloads. */
if (!customElements.get('gomila-editorial')) {
  customElements.define('gomila-editorial', class extends HTMLElement {
    connectedCallback() {
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.animations = new Set();
      this.onPreferenceChange = () => this.startMotion();
      this.motionPreference.addEventListener('change', this.onPreferenceChange);
      this.startMotion();
    }

    startMotion() {
      this.stopMotion();
      if (this.motionPreference.matches || !('IntersectionObserver' in window)) return;
      const groups = [
        ['.ge-hero-copy > *', 90],
        ['.ge-section-head, .ge-category, .ge-product-entry', 85],
        ['.ge-feature-copy > *, .ge-finale > *', 75],
        ['.ge-styles-title, .ge-style, .ge-video', 70]
      ];
      const delays = new Map();
      for (const [selector, interval] of groups) {
        this.querySelectorAll(selector).forEach((element, index) => delays.set(element, (index % 4) * interval));
      }
      // Category cards read best cascading left-to-right, so give them their own ramp.
      this.querySelectorAll('.ge-category').forEach((element, index) => delays.set(element, index * 150));
      this.revealObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const element = entry.target;
          this.revealObserver.unobserve(element);
          // Keep keyboard-focused controls fully visible while navigating.
          if (element.matches(':focus-within')) continue;
          const image = element.matches('.ge-hero-image, .ge-feature-image');
          const ornament = element.matches('.ge-statement > span:nth-child(2)');
          const category = element.matches('.ge-category');
          const frames = image
            ? [{ clipPath: 'inset(0 0 12% 0)' }, { clipPath: 'inset(0 0 0 0)' }]
            : ornament
              ? [{ transform: 'rotate(-120deg) scale(.7)' }, { transform: 'rotate(0) scale(1)' }]
              : category
                ? [
                    { opacity: 0, transform: 'translateY(56px) scale(.965)', clipPath: 'inset(14% 0 0 0)' },
                    { opacity: 1, transform: 'translateY(0) scale(1)', clipPath: 'inset(0 0 0 0)' }
                  ]
                : [{ opacity: 0, transform: 'translateY(28px)' }, { opacity: 1, transform: 'translateY(0)' }];
          const animation = element.animate(frames, {
            duration: image ? 1100 : category ? 1150 : 800,
            delay: delays.get(element) || 0,
            easing: 'cubic-bezier(.16,1,.3,1)',
            fill: 'backwards'
          });
          this.animations.add(animation);
          animation.finished.then(() => this.animations.delete(animation), () => this.animations.delete(animation));
        }
      }, { threshold: 0.08 });
      for (const element of delays.keys()) this.revealObserver.observe(element);
      this.querySelectorAll('.ge-hero-image, .ge-feature-image, .ge-statement > span:nth-child(2)').forEach(element => this.revealObserver.observe(element));
      this.onFocus = () => this.animations.forEach(animation => animation.finish());
      this.addEventListener('focusin', this.onFocus);

      // Category cards drift at staggered rates while their section is on screen.
      this.categories = Array.from(this.querySelectorAll('.ge-category-image'));
      this.categoriesVisible = false;
      if (this.categories.length) {
        this.categoryObserver = new IntersectionObserver(entries => {
          this.categoriesVisible = entries.some(entry => entry.isIntersecting);
          if (this.categoriesVisible) this.onScroll?.();
        });
        this.categories.forEach(element => this.categoryObserver.observe(element));
      }

      this.driftCategories = () => {
        if (!this.categoriesVisible) return;
        for (let i = 0; i < this.categories.length; i += 1) {
          const element = this.categories[i];
          const rect = element.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > innerHeight) continue;
          const progress = (innerHeight / 2 - rect.top - rect.height / 2) / innerHeight;
          // Middle card moves most, giving the row a gentle wave.
          const depth = i % 2 === 1 ? 26 : 13;
          element.style.setProperty('--ge-card-drift', `${Math.max(-1, Math.min(1, progress)) * depth}px`);
        }
      };

      // A small scroll-linked image drift, only while the hero is on screen.
      this.hero = this.querySelector('.ge-hero-image');
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.driftCategories();
          if (!this.hero || !this.heroVisible) return;
          const rect = this.hero.getBoundingClientRect();
          const progress = Math.max(-1, Math.min(1, (innerHeight / 2 - rect.top - rect.height / 2) / innerHeight));
          this.hero.style.setProperty('--ge-drift', `${progress * 12}px`);
        });
      };
      if (this.hero) {
        this.heroObserver = new IntersectionObserver(entries => {
          this.heroVisible = entries[0].isIntersecting;
          if (this.heroVisible) this.onScroll();
        });
        this.heroObserver.observe(this.hero);
      }
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    }

    stopMotion() {
      this.revealObserver?.disconnect();
      this.heroObserver?.disconnect();
      this.categoryObserver?.disconnect();
      this.categories?.forEach(element => element.style.removeProperty('--ge-card-drift'));
      this.categoriesVisible = false;
      this.animations?.forEach(animation => animation.cancel());
      this.animations?.clear();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.onFocus) this.removeEventListener('focusin', this.onFocus);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.heroVisible = false;
      this.hero?.style.removeProperty('--ge-drift');
    }

    disconnectedCallback() {
      this.stopMotion();
      this.motionPreference?.removeEventListener('change', this.onPreferenceChange);
    }
  });
}
